import React, { useEffect } from 'react'
import './App.css'
import ReactGA from 'react-ga'
import AppHeader from './components/header/AppHeader'
import { useLocation } from 'react-router-dom'
import ContentContainer from './components/common/ContentContainer'
import { useDispatch, useSelector } from 'react-redux'
import IdleSessionTimeout from './components/session/IdleSessionTimeout'
import Routes from './config/Routes'
import { resetForm } from './state/slice/form'
import SitusStateChangeListenerModal from './components/form/SitusStateChangeListenerModal'

export const LEAVE_FORM_PROMPT = 'Leaving this page will clear all form data. Are you sure you want to proceed?'

ReactGA.initialize('UA-40200174-2', {
  testMode: process.env.NODE_ENV === 'test',
  /**
   * To enable or disable the console logging of tracking calls when running locally,
   */
  debug: false
})

export default function App () {
  const formOptions = useSelector(state => state.form.options)
  const sessionLive = useSelector(state => state.form.context.sessionLive)
  const location = useLocation()
  const dispatch = useDispatch()

  const formReady = () => {
    return formOptions.homeState.value &&
      formOptions.applicantType.value &&
      formOptions.groupType.value &&
      [formOptions.life.value, formOptions.disability.value, formOptions.ci.value]
        .some(p => p)
  }

  useEffect(() => {
    ReactGA.set({ page: location.pathname })
    ReactGA.pageview(location.pathname)
    window.history.pushState(null, document.title, window.location.href)
  }, [location])

  useEffect(() => {
    if (sessionLive) {
      /**
       * Two handlers to handle both refresh and clicking the back button.
       *
       * When event is null, nothing will happen. Both event listeners are null when session is not live,
       *    and users can refresh or click 'Back' with no prompt.
       *
       * On a page refresh, the 'beforeunload' is triggered as the resources are unloaded,
       *    and we can prevent it from happening by simply setting the event to a function.
       *    Note: We cannot show a custom prompt message here, or do any custom logic.
       *      All modern browsers have limited this capability due to security concerns.
       *
       * Detecting 'Back' button click, and prompting user:
       *    In a SPA (like this react app with react-router routes),
       *      the page never 'unloads' all the resources unless a full refresh is done.
       *    Thus, clicking the 'Back' button will not trigger the beforeunload event.
       *    To solve this:
       *      - Set a 'fake' history entry on location change, inserting the new location into history,
       *          so that clicking 'Back' navigates to the same current location, i.e. does nothing.
       *          (See the effect hook listening to location changes)
       *      - Additionally, add the fake history entry when user opts to stay on the page,
       *          so subsequent back clicks act the same.
       *      - If user confirms prompt on back button click, treat it like a refresh,
       *          resetting state and allowing navigation to previous location.
       *
       *    Note: the popstate event is not Cancellable, thus we cannot call preventDefault() to handle this.
       */
      window.onbeforeunload = (e) => e.returnValue = ''
      window.onpopstate = () => {
        if (window.confirm(LEAVE_FORM_PROMPT)) {
          dispatch(resetForm())
        } else {
          window.history.pushState(null, document.title, window.location.href)
        }
      }
    } else {
      window.onbeforeunload = null
      window.onpopstate = null
    }
    // eslint-disable-next-line
  }, [sessionLive])

  return <>
    <AppHeader />
    <IdleSessionTimeout />
    <SitusStateChangeListenerModal />
    <ContentContainer>
      <Routes formReadyToBegin={formReady} />
    </ContentContainer>
  </>
}
